import { Head, Link, router } from "@inertiajs/react";
import { Layout } from "@/components/Layout";
import type { BookedCourt, Booking, BookingRequest, BookingSeries } from "@/lib/types";

// One list, not two. A request and the court it won are the same thing to the
// member, and splitting them into "Booked" and "Requests" made a single
// booking read as two unrelated rows — one saying fulfilled, one saying
// confirmed, with nothing tying them together.
export default function Dashboard({
  series,
  requests,
  bookings,
  hidden,
}: {
  series: BookingSeries[];
  requests: BookingRequest[];
  bookings: Booking[];
  hidden: number;
}) {
  const empty = requests.length === 0 && bookings.length === 0 && series.length === 0;

  return (
    <Layout>
      <Head title="Your courts" />

      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Your courts</h1>
        <Link
          href="/booking_requests/new"
          className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          New request
        </Link>
      </div>

      {series.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-neutral-500">
            Repeating
          </h2>
          <ul className="space-y-2">
            {series.map((entry) => (
              <SeriesRow key={entry.id} series={entry} />
            ))}
          </ul>
        </section>
      )}

      {series.length > 0 && requests.length + bookings.length > 0 && (
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-neutral-500">
          Your courts
        </h2>
      )}

      {empty ? (
        <Empty>Nothing yet — ask for a court and Court Pilot will take it from there.</Empty>
      ) : (
        <ul className="space-y-2">
          {requests.map((request) => (
            <RequestRow key={request.id} request={request} />
          ))}

          {/* A court whose request has been replaced is still a court you
              hold, so it keeps its place in the list rather than vanishing. */}
          {bookings.map((booking) => (
            <li
              key={booking.id}
              className="rounded-lg border border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900"
            >
              <div className="flex items-center justify-between">
                <div className="font-medium">
                  {formatDate(booking.date)} · {booking.startTime}
                  <span className="ml-2 text-neutral-500">{booking.duration}min</span>
                </div>
                <Badge tone="success">booked</Badge>
              </div>
              <div className="mt-1 text-sm text-neutral-500">{booking.court}</div>
            </li>
          ))}
        </ul>
      )}

      {/* Hidden, not lost. Silently dropping rows would have somebody hunting
          for a booking they made last month and concluding it never happened. */}
      {hidden > 0 && (
        <p className="mt-6 text-sm text-neutral-500">
          {hidden} older {hidden === 1 ? "entry is" : "entries are"} hidden. Games
          more than three days past are kept but not shown here.
        </p>
      )}
    </Layout>
  );
}

function SeriesRow({ series }: { series: BookingSeries }) {
  return (
    <li
      className={`rounded-lg border border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900 ${
        series.paused ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="font-medium">
          {series.summary}
        </div>
        {/* The only thing that really matters about a standing game: has it
            actually been getting courts? */}
        <span className="shrink-0 text-sm text-neutral-500">
          {series.total > 0
            ? `booked ${series.booked}/${series.total}`
            : "no weeks yet"}
        </span>
      </div>

      <div className="mt-1 flex items-center justify-between gap-3">
        <span className="text-sm text-neutral-500">
          {series.club}
          {series.paused ? " · paused" : ` · next ${formatDate(series.nextDate)}`}
        </span>

        <span className="flex shrink-0 gap-3 text-sm">
          <button
            type="button"
            className="text-neutral-500 underline-offset-2 hover:underline"
            onClick={() =>
              router.patch(`/repeats/${series.id}`, { paused: !series.paused })
            }
          >
            {series.paused ? "resume" : "pause"}
          </button>
          <button
            type="button"
            className="text-red-600 underline-offset-2 hover:underline dark:text-red-400"
            onClick={() => {
              if (confirm("Stop repeating? Courts already booked are kept.")) {
                router.delete(`/repeats/${series.id}`);
              }
            }}
          >
            stop
          </button>
        </span>
      </div>
    </li>
  );
}

function RequestRow({ request }: { request: BookingRequest }) {
  return (
    <Link
      as="li"
      href={`/booking_requests/${request.id}`}
      className="block cursor-pointer rounded-lg border border-neutral-200 bg-white px-4 py-3 transition hover:border-neutral-300 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-neutral-700"
    >
      <div className="flex items-center justify-between">
        <div className="font-medium">
          {formatDate(request.date)} · {request.startTime}
          <span className="ml-2 text-neutral-500">
            {request.duration}min {request.sport}
          </span>
          {/* Says where this row came from, so a week nobody asked for by hand
              does not look like a mystery. */}
          {request.repeating && (
            <span className="ml-2 text-neutral-400" title="Part of a weekly repeat">⟳</span>
          )}
          {request.backupTimes.length > 0 && (
            <span className="ml-2 text-xs text-neutral-400" title="Alternative times tried in order">
              or {request.backupTimes.join(", ")}
            </span>
          )}
        </div>
        <Badge tone={toneFor(request)}>{statusLabel(request)}</Badge>
      </div>

      {/* What it got, when it got something. */}
      {request.courts.length > 0 ? (
        <div className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">
          {origin(request)}{request.courts.map(describeCourt).join(" + ")}
        </div>
      ) : (
        <div className="mt-1 text-sm text-neutral-500">{waitingLine(request)}</div>
      )}

      {request.lastError && request.courts.length === 0 && (
        <div className="mt-1 text-sm text-neutral-400">{request.lastError}</div>
      )}
    </Link>
  );
}

// A two-hour session is two courts under two members, so say both rather than
// showing only the first and looking like half a booking.
function describeCourt(court: BookedCourt) {
  const held = court.status === "holding" ? " (holding)" : "";
  return `${court.court ?? "court"} at ${court.startTime}, ${court.duration}min${held}`;
}

function waitingLine(request: BookingRequest) {
  // Terminal states first: what a finished request WOULD have done is not
  // worth saying, and saying it reads as though it were still running.
  if (request.lostCourt) return `${origin(request)}This court was cancelled at David Lloyd.`;
  if (request.status === "cancelled") return `${origin(request)}Cancelled.`;
  if (request.status === "already_booked") return `${origin(request)}You already had a court that day.`;
  if (request.status === "partial") {
    return `${origin(request)}Booked what was free at release — the full ${request.duration} minutes was not available, so this is the most it could get.`;
  }
  if (request.status === "failed") return `${origin(request)}No court found.`;

  const how =
    request.mode === "sniper" ? "Watching for cancellations" : "Booking at release";
  const when = request.released ? how : `${how} · opens ${formatDateTime(request.releasesAt)}`;

  return `${origin(request)}${when}`;
}

// A week a standing game produced looks exactly like a second booking for the
// same slot unless it says otherwise — the repeat marker alone was too quiet
// to carry it.
function origin(request: BookingRequest) {
  if (!request.repeating) return "";

  const day = new Date(`${request.date}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long" });

  return `Your weekly ${day} game · `;
}

function toneFor(request: BookingRequest) {
  if (request.lostCourt) return "neutral";
  if (request.status === "fulfilled") return "success";
  if (request.status === "failed" || request.status === "blocked") return "error";
  return "pending";
}

// "fulfilled" would claim credit for a court the member booked themselves.
const STATUS_LABELS: Record<string, string> = {
  pending: "waiting",
  processing: "trying now",
  partial: "part booked",
  fulfilled: "booked",
  already_booked: "you already had a court",
  failed: "not booked",
  cancelled: "cancelled",
  blocked: "blocked",
};

function statusLabel(request: BookingRequest) {
  // Still saying "booked" for a court that has been cancelled is the same
  // failure as the dashboard once showing a court that no longer existed.
  if (request.lostCourt) return "cancelled";

  return STATUS_LABELS[request.status] ?? request.status;
}

function Badge({ tone, children }: {
  tone: "success" | "pending" | "error" | "neutral";
  children: string;
}) {
  const tones = {
    success: "bg-brand-50 text-brand-700 dark:bg-brand-700/20 dark:text-brand-50",
    pending: "bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
    error: "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-200",
    neutral: "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300",
  };

  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

function Empty({ children }: { children: string }) {
  return (
    <p className="rounded-lg border border-dashed border-neutral-300 px-4 py-8 text-center text-sm text-neutral-500 dark:border-neutral-700">
      {children}
    </p>
  );
}

function formatDate(iso: string) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
